import { useState } from 'react';
import { useT } from '@kmgestao/i18n';
import { Badge, Button, PageContainer, PageIntroCard, PageSectionCard } from '@kmgestao/ui';

import { useCurrentClinicId } from '../../../features/auth/index.js';
import { ClinicShell } from '../ClinicShell.js';

import { InviteMemberDialog } from './InviteMemberDialog.js';
import { TeamMembersTable } from './TeamMembersTable.js';

/**
 * Página `/equipe` — gestão de membros da clínica (PR-M2-03).
 *
 * Acesso: `clinic_owner` / `clinic_admin` via `<RequireClinicAdmin>`.
 * Rota fica DENTRO do gate de pagamento (`RequireClinicSubscriptionActive`)
 * porque convidar membros é função operacional — owner em
 * payment-pending ainda não tem plataforma liberada.
 *
 * Conteúdo:
 *   - Botão "Convidar membro" (abre dialog com form RHF + Zod)
 *   - Tabela de membros (nome, email, papel, status, ações)
 *   - Ações por linha: suspender (active → suspended) / reativar
 *     (suspended → active). Owner = read-only. Caller próprio = sem
 *     auto-suspensão.
 *
 * Edge function `invite-clinic-member` faz o trabalho pesado: cria
 * user + member + magic link + email `welcome_clinic_member`.
 *
 * **Padrão canônico** das páginas internas (`PageContainer` +
 * `PageIntroCard` + `PageSectionCard`).
 */
export function ClinicTeamPage() {
  const { t } = useT('clinics');
  const clinicId = useCurrentClinicId();
  const [inviteOpen, setInviteOpen] = useState(false);

  return (
    <ClinicShell>
      <PageContainer>
        <PageIntroCard
          eyebrow={t('team.eyebrow')}
          title={t('team.title')}
          description={t('team.description')}
          meta={<Badge variant="outline">{t('team.metaBadge')}</Badge>}
        />

        {!clinicId ? (
          <PageSectionCard
            title={t('team.noClinic.title')}
            description={t('team.noClinic.description')}
          >
            <div />
          </PageSectionCard>
        ) : (
          <>
            <PageSectionCard
              title={t('team.list.title')}
              description={t('team.list.description')}
              contentClassName="space-y-4"
            >
              <div className="flex justify-end">
                <Button type="button" onClick={() => setInviteOpen(true)}>
                  {t('team.invite.openButton')}
                </Button>
              </div>
              <TeamMembersTable clinicId={clinicId} />
            </PageSectionCard>

            <InviteMemberDialog
              open={inviteOpen}
              onOpenChange={setInviteOpen}
              clinicId={clinicId}
            />
          </>
        )}
      </PageContainer>
    </ClinicShell>
  );
}
